import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Button,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material';
import { Title, Text, DetailPopup } from 'shared/components';
import {
  Clear as ClearIcon,
  Error as ErrorIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import {
  Bottom,
  Chip,
  CloneDashboardContainer,
  Content,
  Divider,
  Form,
  FormTextFieldContainer,
  Header,
} from './styles';

const defaultSettings = {
  beneficiaries: {
    metric: 'Report Count',
    segment: 'By Portfolio',
    filter: 'All time',
    year: [],
    beneficiaries: [53, 37, 52, 36, 51, 35],
    switch: 'Any',
    compare: '',
    comparisonyear: 'No Comparison',
    status: 'Active'
  },
  donations: {
    segment: 'Portfolio',
    filter: 'All time',
    year: [],
    donationType: 'All Types',
    switch: '',
    comparisonyear: 'None',
    compare: 'No Comparison'
  },
  reportingCompliance: {
    segment: 'Portfolio',
    filter: 'All time',
    year: [],
    invitation_ago: 'No limit',
    comparisonyear: 'No Comparison',
    compare: ''
  },
  dataQuality: {
    segment: 'NPO',
    filter: 'All time',
    year: [],
    comparisonyear: 'No Comparison',
    compare: ''
  },
  impacts: {
    metric: 'Total claim',
    segment: 'Portfolio',
    filter: 'All time',
    year: [],
    indicators: [
      'gain STEM credential',
      'improve academic performance',
      'improve health',
      'become advocates/patrons',
      'gain employment',
      'lbs. of materials recycled',
      'realize other benefit from stewardship program (custom definition)',
    ],
    catalized: true,
    portfolios: [],
    services: [],
    allocation: 'All'
  }
};

const DashboardAddModal = ({
  open,
  onClose,
  onAdd,
  dashboards,
  current,
  initial
}) => {
  const [operation, setOperation] = React.useState('new');
  const [dashboard, setDashboard] = React.useState(null);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (initial !== null) {
      setName(`${initial.name} #2`);
      setDescription(`${initial.description} #2`);
      setOperation('copy');
      setDashboard(initial);
    }
  }, [initial]);

  const handleChangeDashboard = event => {
    setDashboard(event.target.value);
    setName(`${event.target.value.name} #2`);
    setDescription(event.target.value.description);
  };

  const handleOnChangeName = event => {
    setName(event.target.value);
  };

  const handleOnChangeDescription = event => {
    setDescription(event.target.value);
  };

  const handleChangeOperation = event => {
    setOperation(event.target.value);
  };

  const handleCloseModal = () => {
    setOperation('new');
    setDashboard(null);
    setName('');
    setDescription('');
    setError('');
    onClose();
  };

  const handleSave = () => {
    const board = {
      name,
      description,
      cards: [
        {
          id: uuidv4(),
          title: 'Impacts',
          settings: defaultSettings.impacts
        },
        {
          id: uuidv4(),
          title: 'Donations',
          settings: defaultSettings.donations
        },
        {
          id: uuidv4(),
          title: 'Reporting Compliance',
          settings: defaultSettings.reportingCompliance
        },
        {
          id: uuidv4(),
          title: 'Data Quality',
          settings: defaultSettings.dataQuality
        },
        {
          id: uuidv4(),
          title: 'Beneficiaries',
          settings: defaultSettings.beneficiaries
        }
      ]
    };
    if (!name) {
      return setError('A name is required');
    }
    if (!description) {
      return setError('A description is required');
    }
    const resultNames = dashboards.filter(dash => dash.name === board.name);
    if (resultNames.length > 0) {
      return setError(
        'There is already a Dashboard with that name, choose a different one'
      );
    }
    if (operation === 'copy' && !dashboard) {
      return setError('A dashboard is required');
    }
    if (operation === 'current') {
      board.cards = current.cards;
    }
    if (operation === 'copy') {
      board.cards = dashboard.cards;
    }
    onAdd(board);
    return handleCloseModal();
  };

  return (
    <DetailPopup
      open={open}
      onClose={handleCloseModal}
      insideCard={false}
      width={'400px'}
    >
      <Content>
        <Header>
          <Title gutterBottom>Add New Dashboard</Title>
          <Divider />
        </Header>
        <Form component="fieldset">
          <FormLabel component="legend">
            How do you want to initialize your dashboard?
          </FormLabel>
          <RadioGroup
            aria-label="gender"
            name="gender1"
            value={operation}
            onChange={handleChangeOperation}
          >
            <FormControlLabel
              value="new"
              control={<Radio />}
              label={<Text>Create new dashboard from scratch</Text>}
            />
            <FormControlLabel
              value="current"
              control={<Radio />}
              label={<Text>Create dashboard from current dashboard</Text>}
              disabled={dashboards.length === 0}
              style={{ fontSize: 12 }}
            />
            <CloneDashboardContainer>
              <FormControlLabel
                value="copy"
                control={<Radio />}
                label={<Text>Create dashboard from other dashboard</Text>}
                disabled={dashboards.length === 0}
                style={{ fontSize: 12 }}
              />
              {operation === 'copy' && (
                <TextField
                  id="outlined-select-dashboard"
                  select
                  label="Select a dashboard to copy"
                  onChange={handleChangeDashboard}
                  variant="outlined"
                  size="small"
                  style={{ minWidth: 500, width: 500, marginLeft: 10 }}
                >
                  {dashboards.map(board => (
                    <MenuItem key={board.id} value={board}>
                      {board.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </CloneDashboardContainer>
          </RadioGroup>
          <Divider />
          <FormTextFieldContainer>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={handleOnChangeName}
                  variant="outlined"
                  size="small"
                  fullWidth
                  inputProps={{ style: { fontSize: 14 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description (200 characters)"
                  multiline
                  rows={2}
                  value={description}
                  onChange={handleOnChangeDescription}
                  inputProps={{ maxLength: 200, style: { fontSize: 14 } }}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </FormTextFieldContainer>
        </Form>
        <Bottom>
          {error && (
            <Chip
              icon={<ErrorIcon />}
              label={error}
              color="secondary"
              variant="outlined"
            />
          )}
          <Button
            variant="outlined"
            color="primary"
            endIcon={<ClearIcon />}
            onClick={handleCloseModal}
            style={{ marginRight: 5 }}
          >
            CANCEL
          </Button>
          <Button
            endIcon={<SaveIcon />}
            onClick={handleSave}
          >
            SAVE
          </Button>
        </Bottom>
      </Content>
    </DetailPopup>
  );
};

DashboardAddModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onAdd: PropTypes.func,
  dashboards: PropTypes.array,
  current: PropTypes.object,
  initial: PropTypes.object,
  noCloseIcon: PropTypes.bool,
  isNew: PropTypes.bool,
};

export default DashboardAddModal;
